import {
  EntityListFilterByOperator,
  Space,
  SpaceType,
} from '@tribeplatform/gql-client/types'
import { useI18n } from '@tribeplatform/react-components/i18n'
import {
  useAuthMember,
  useCollections,
  useSpaces,
} from '@tribeplatform/react-sdk/hooks'
import { simplifyPaginatedResult } from '@tribeplatform/react-sdk/utils'

import {
  CollectionMenuEntry,
  CollectionMenuItem,
  CollectionMenuSpaceFilter,
  CollectionMenuViewStyle,
  NO_COLLECTION_ID,
} from './types.js'
import { buildNoCollectionEntry } from './utils.js'

const SPACES_LIMIT = 100

export const useCollectionMenuEntries = ({
  items,
  viewStyle,
  spaceFilter,
}: {
  items?: CollectionMenuItem[]
  viewStyle: CollectionMenuViewStyle
  spaceFilter: CollectionMenuSpaceFilter
}): {
  entries: CollectionMenuEntry[]
  isLoading: boolean
  isReady: boolean
} => {
  const { $t } = useI18n()
  const { data: member, isGuest } = useAuthMember()

  const noCollectionEnabled = !!items?.find(
    item => item.id === NO_COLLECTION_ID,
  )?.enabled

  const spaceFields =
    viewStyle === CollectionMenuViewStyle.Simple
      ? {}
      : {
          image: 'basic' as const,
          banner: 'basic' as const,
          slate: 'all' as const,
          authMemberProps: {},
        }

  const exploreOnly =
    !isGuest && spaceFilter === CollectionMenuSpaceFilter.NotJoined
  const memberId =
    !isGuest && spaceFilter === CollectionMenuSpaceFilter.Joined
      ? member?.id
      : null

  const { data: collections, isInitialLoading: isCollectionsLoading } =
    useCollections({
      fields: {
        spaces: {
          fields: spaceFields,
          variables: { limit: SPACES_LIMIT, exploreOnly, memberId },
        },
      },
      variables: {},
      useQueryOptions: { retry: 10, retryDelay: 1000 },
    })

  const { data: orphanSpacesData, isInitialLoading: isOrphanSpacesLoading } =
    useSpaces({
      variables: {
        limit: SPACES_LIMIT,
        type: [SpaceType.Group, SpaceType.Broadcast],
        filterBy: [
          {
            key: 'groupId',
            operator: EntityListFilterByOperator.hasValue,
            value: 'false',
          },
        ],
        exploreOnly,
        memberId,
      },
      fields: spaceFields,
      useInfiniteQueryOptions: { enabled: noCollectionEnabled },
    })

  const isReady = Array.isArray(collections)
  const isLoading =
    isCollectionsLoading || (noCollectionEnabled && isOrphanSpacesLoading)

  if (!isReady) {
    return { entries: [], isLoading, isReady }
  }

  if (!noCollectionEnabled) {
    return { entries: collections, isLoading, isReady }
  }

  const orphanSpaces = simplifyPaginatedResult<Space>(orphanSpacesData)
  const noCollectionName = $t({
    id: 'Generics.NoCollection',
    defaultMessage: 'No {COLLECTION}',
  }) as string

  const entries: CollectionMenuEntry[] = [
    ...collections,
    buildNoCollectionEntry(noCollectionName, {
      nodes: orphanSpaces.nodes,
      totalCount: orphanSpaces.totalCount,
    }),
  ]

  return { entries, isLoading, isReady }
}
